import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Select } from 'antd';
import { useAttendanceForm } from './hooks/useAttendanceForm';

const STATUS_OPTIONS = [
  { value: '', label: '(無)' },
  { value: 'N', label: 'N - 免刷卡' },
  { value: 'H', label: 'H - 休假' },
  { value: 'M', label: 'M - 調休假' },
  { value: 'W', label: 'W - 調上班' },
];

const PW9210F1Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useAttendanceForm();

  const [searchText, setSearchText] = useState('');
  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${current.f910_01}」於 ${(current.f910_02 || '').substring(0, 10)} 的出勤記錄？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };
  const handleQuickSearch = () => { if (searchText.trim()) quickSearch(searchText.trim()); };
  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const lw = 140; // label width

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          每日出勤記錄 (pw9210f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="員工代號或備註" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch} disabled={isEditing} style={{ width: 300 }} />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-3 mb-6">
          {/* PK fields */}
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">員工代號 (F910_01) <span className="text-red-500">*</span></label>
            <Input value={form.f910_01} onChange={e => setField('f910_01', e.target.value)}
              maxLength={8} disabled={mode !== 'add'} style={{ width: 160 }} placeholder="最多8字元" />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">出勤日期 (F910_02) <span className="text-red-500">*</span></label>
            <Input value={form.f910_02} onChange={e => setField('f910_02', e.target.value)}
              disabled={mode !== 'add'} style={{ width: 160 }} placeholder="YYYY-MM-DD" />
          </div>

          {/* Attendance fields */}
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">出勤狀態 (F910_11)</label>
            <Select value={form.f910_11} onChange={v => setField('f910_11', v)}
              options={STATUS_OPTIONS} disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">備註 (F910_14)</label>
            <Input value={form.f910_14} onChange={e => setField('f910_14', e.target.value)}
              maxLength={50} disabled={!isEditing} style={{ width: 400 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">上班時間 (F910_21)</label>
            <Input value={form.f910_21} onChange={e => setField('f910_21', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
            <label className="text-sm text-gray-700 ml-2">下班時間 (F910_22)</label>
            <Input value={form.f910_22} onChange={e => setField('f910_22', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">加班開始 (F910_23)</label>
            <Input value={form.f910_23} onChange={e => setField('f910_23', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 120 }} />
            <label className="text-sm text-gray-700 ml-2">加班結束 (F910_24)</label>
            <Input value={form.f910_24} onChange={e => setField('f910_24', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">遲到(分) (F910_29)</label>
            <Input type="number" value={form.f910_29} onChange={e => setField('f910_29', Number(e.target.value))}
              disabled={!isEditing} style={{ width: 100 }} min={0} />
            <label className="text-sm text-gray-700 ml-2">早退(分) (F910_30)</label>
            <Input type="number" value={form.f910_30} onChange={e => setField('f910_30', Number(e.target.value))}
              disabled={!isEditing} style={{ width: 100 }} min={0} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">簽到密碼 (F910_13)</label>
            <Input.Password value={form.f910_13} onChange={e => setField('f910_13', e.target.value)}
              maxLength={6} disabled={!isEditing} style={{ width: 120 }} />
            <label className="text-sm text-gray-700 ml-2">輪休組別 (F910_16)</label>
            <Input value={form.f910_16} onChange={e => setField('f910_16', e.target.value)}
              maxLength={1} disabled={!isEditing} style={{ width: 80 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">部門代號 (F910_31)</label>
            <Input value={form.f910_31} onChange={e => setField('f910_31', e.target.value)}
              maxLength={4} disabled={!isEditing} style={{ width: 120 }} />
            <label className="text-sm text-gray-700 ml-2">組別 (F910_15)</label>
            <Input value={form.f910_15} onChange={e => setField('f910_15', e.target.value)}
              maxLength={4} disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">應上班 (F910_32)</label>
            <Input value={form.f910_32} disabled style={{ width: 120 }} />
            <label className="text-sm text-gray-700 ml-2">應下班 (F910_33)</label>
            <Input value={form.f910_33} disabled style={{ width: 120 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">請假單號 (F910_17)</label>
            <Input value={form.f910_17} onChange={e => setField('f910_17', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 160 }} />
            <label className="text-sm text-gray-700 ml-2">出差單號 (F910_19)</label>
            <Input value={form.f910_19} onChange={e => setField('f910_19', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div className="flex items-center gap-4">
            <label style={{ width: lw }} className="text-sm font-medium text-gray-700">調班單號 (F910_20)</label>
            <Input value={form.f910_20} onChange={e => setField('f910_20', e.target.value)}
              maxLength={8} disabled={!isEditing} style={{ width: 160 }} />
          </div>
        </div>

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f910_91} | 建立日期: {current.f910_92 ? current.f910_92.substring(0, 10) : '-'}</div>
            <div>修改人員: {current.f910_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default PW9210F1Form;
